import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate, Link } from 'react-router-dom';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { useFacilityStore } from '../../../../app/z-store/store';
import { getItem } from "../../../../utils/localStorage";

const PurchaseOrderTab = () => {
  const navigate = useNavigate();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const [searchTerm, setSearchTerm] = useState('');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  
  // Add user-related state variables like in Purchase Request
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [userName, setUserName] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // New state for comments dialog
  const [commentsDialogVisible, setCommentsDialogVisible] = useState(false);
  const [comments, setComments] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'

  const statusOptions = [
    { label: 'ALL', value: 'ALL' },
    { label: 'PENDING', value: 'PENDING' },
    { label: 'APPROVED', value: 'APPROVED' },
    { label: 'NOT APPROVED', value: 'NOT APPROVED' },
    { label: 'CANCELLED', value: 'CANCELLED' }
  ];

  // Get current user when component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        
        let parsedUser;
        
        // Check if the value needs to be parsed
        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue; // Assume it's already an object
        }
        
        // Safely access and set user details
        if (parsedUser?.user) {
          setCurrentUserId(parsedUser.user.userId);
          setUserRole(parsedUser.user.role);
          setUserName(parsedUser.user.fullName);
          setCurrentUser(parsedUser.user); // Store the entire user object
        } else {
          console.error("Invalid user structure:", parsedUser);
        }
      } catch (err) {
        console.log("Error getting user:", err.message);
      }
    };
    
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (selectedFacilityId) fetchPurchaseOrders();
  }, [selectedFacilityId]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/app/purchase-orders/all/${selectedFacilityId}`,
        'GET'
      );
      if (response.success) {
        setPurchaseOrders(response.data?.data || []);
      } else {
        setPurchaseOrders([]);
      }
    } catch (error) {
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is an approver for a specific purchase order
  const isUserApprover = (order) => {
    if (!currentUser?.userId || !Array.isArray(order.approvals)) return false;
  
    // find the approvals object for the current step
    const step = order.approvals.find(s => s.stepNumber === order.currentStep);
    if (!step || !Array.isArray(step.approvers)) return false;
  
    return step.approvers.some(approver =>
      String(approver.userId) === currentUser.userId &&
      approver.status === 'pending'
    );
  };

  // Check if request has been approved by the current user
  const hasUserApproved = (order) => {
    if (!currentUser?.userId || !order.approvals) return false;
    
    // Check if user has already approved in any step
    for (const step of order.approvals) {
      if (!Array.isArray(step.approvers)) continue;
      
      const hasApproved = step.approvers.some(approver => 
        String(approver.userId) === currentUser.userId && 
        approver.status === 'approved'
      );
      
      if (hasApproved) return true;
    }
    
    return false;
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const filteredOrders = Array.isArray(purchaseOrders)
    ? purchaseOrders.filter((item) => {
        const poNumber = (item.poNumber || '').toLowerCase();
        // Use supplierInfo.name instead of supplier directly
        const supplierName = ((item.supplierInfo && item.supplierInfo.name) || '').toLowerCase();
        const fromField = (item.from || '').toLowerCase();
        
        const textFilterPassed =
          poNumber.includes(searchTerm.toLowerCase()) ||
          supplierName.includes(searchTerm.toLowerCase()) ||
          fromField.includes(searchTerm.toLowerCase());
          
        const statusFilterPassed = statusFilter === 'ALL' || item.status.toUpperCase() === statusFilter;
        
        return textFilterPassed && statusFilterPassed;
      })
    : [];

  const formatCurrency = (value) =>
    value !== null && value !== undefined
      ? `KES ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : 'KES 0.00';
      
  // Date formatting function to format as DD-MONTH-YYYY
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Template to display supplier name from supplierInfo
  const supplierTemplate = (rowData) => {
    return rowData.supplierInfo?.name || 'N/A';
  };

  const statusBodyTemplate = (rowData) => {
    const statusStyles = {
      approved: { color: '#28a745', backgroundColor: '#e9fbe9' },
      pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
      'not approved': { color: '#dc3545', backgroundColor: '#fde9e9' },
      cancelled: { color: '#6c757d', backgroundColor: '#f1f1f1' }
    };
    
    const status = rowData.status.toLowerCase();
    const style = statusStyles[status] || { color: '#6c757d', backgroundColor: '#f1f1f1' };
    
    return (
      <span
        style={{
            ...style,
            padding: '0.5em 1em',
            borderRadius: '12px',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            display: 'inline-block',
        }}
      >
        {rowData.status}
      </span>
    );
  };

  // Open comments dialog for approval/rejection
  const openCommentsDialog = (id, action) => {
    setSelectedOrderId(id);
    setApprovalAction(action);
    setComments('');
    setCommentsDialogVisible(true);
  };

  // Submit approval/rejection with comments - EXACT COPY from Purchase Requests
  const submitApprovalAction = async () => {
    if (!selectedOrderId || !approvalAction) return;
    
    try {
      setLoading(true);
      setCommentsDialogVisible(false);
      
      // Determine endpoint and parameters based on action
      const endpoint = `/api/app/purchase-orders/approve/${selectedFacilityId}/${selectedOrderId}`;
      const approve = approvalAction === 'approve';
      
      const response = await makeRequest2(
        endpoint,
        'PUT',
        { 
          approve,
          comments,
          userId: currentUser.userId 
        }
      );
      
      if (response.success) {
        toastify(
          response.message || 
          `Purchase order ${approve ? 'approved' : 'rejected'} successfully`, 
          'success'
        );
        fetchPurchaseOrders();
      } else {
        toastify(
          response.error || 
          `Failed to ${approve ? 'approve' : 'reject'} purchase order`, 
          'error'
        );
      }
    } catch (error) {
      console.error("Action error:", error);
      toastify(`Error ${approvalAction === 'approve' ? 'approving' : 'rejecting'} purchase order`, 'error');
    } finally {
      setLoading(false);
      setSelectedOrderId(null);
      setApprovalAction(null);
    }
  };

  // Action template - EXACT COPY from Purchase Requests
  const actionTemplate = (rowData) => {
    const isApprover = isUserApprover(rowData);
    const hasApproved = hasUserApproved(rowData);
    const isPending = (rowData.approvalStatus || '').toLowerCase() === 'pending' || 
                      (rowData.approvalStatus || '').toLowerCase() === 'in_progress';
    
    // If user has already approved, show only view button
    if (hasApproved) {
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-info"
                    onClick={() => handleViewDetails(rowData._id)}
                    tooltip="View"
                />
            </div>
        );
    }
    
    // If user is an approver and request is in pending approval state
    if (isApprover && isPending) {
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-info"
                    onClick={() => handleViewDetails(rowData._id)}
                    tooltip="View"
                />
                <Button
                    icon="ti ti-check"
                    className="p-button-rounded p-button-success"
                    onClick={() => openCommentsDialog(rowData._id, 'approve')}
                    tooltip="Approve"
                />
                <Button
                    icon="ti ti-x"
                    className="p-button-rounded p-button-danger"
                    onClick={() => openCommentsDialog(rowData._id, 'reject')}
                    tooltip="Reject"
                />
            </div>
        );
    }
    
    // For regular users or when request is not pending
    return (
        <div className="d-flex gap-2">
            <Button
                icon="ti ti-eye"
                className="p-button-rounded p-button-info"
                onClick={() => handleViewDetails(rowData._id)}
                tooltip="View"
            />
            <Button
                icon="ti ti-edit"
                className="p-button-rounded p-button-warning"
                onClick={() => handleEditOrder(rowData._id)}
                tooltip="Edit"
                disabled={(rowData.status || '').toLowerCase() !== 'pending'}
            />
        </div>
    );
  };

  const handleViewDetails = (orderId) => {
    navigate(`/facility/procurement_management/purchase_order/view/${orderId}`);
  };

  const handleEditOrder = (orderId) => {
    navigate(`/facility/procurement_management/purchase_order/edit/${orderId}`);
  };

  // Keep the old approve/reject handlers for backward compatibility if needed
  const handleApprove = (orderId) => {
    openCommentsDialog(orderId, 'approve');
  };

  const handleReject = (orderId) => {
    openCommentsDialog(orderId, 'reject');
  };

  // Dialog footer for comments
  const commentsDialogFooter = (
    <div>
      <Button 
        label="Cancel" 
        icon="pi pi-times" 
        className="p-button-text" 
        onClick={() => setCommentsDialogVisible(false)} 
      />
      <Button 
        label={approvalAction === 'approve' ? 'Approve' : 'Reject'} 
        icon={approvalAction === 'approve' ? 'pi pi-check' : 'pi pi-times'} 
        className={approvalAction === 'approve' ? 'p-button-success' : 'p-button-danger'} 
        onClick={submitApprovalAction} 
      />
    </div>
  );

  if (!selectedFacilityId) {
    return (
      <div className="card">
        <div className="card-body text-center">
          Please select a facility to view purchase orders.
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog />
      <div className="card">
        <div className="card-body">
          {/* Search and Add Button */}
          <div className="row mb-3">
            <div className="col-md-4">
              <input
                className="form-control"
                placeholder="Search by PO number, supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-8 text-end">
              <button
                className="btn btn-primary"
                onClick={() =>
                  navigate('/facility/procurement_management/purchase_orders/add')
                }
              >
                Add Purchase Order
              </button>
            </div>
          </div>

          {/* Status Filter Buttons */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex flex-wrap gap-2">
                {statusOptions.map(option => (
                  <button
                    key={option.value}
                    className={`btn ${statusFilter === option.value ? 'btn-primary' : 'btn-outline-secondary'} text-uppercase`}
                    onClick={() => handleStatusFilterChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
                <div className="dropdown">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary dropdown-toggle" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                  >
                    MORE
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button 
                        type="button" 
                        className="dropdown-item"
                        onClick={() => handleStatusFilterChange('REJECTED')}
                      >
                        Rejected
                      </button>
                    </li>
                    <li>
                      <button 
                        type="button" 
                        className="dropdown-item"
                        onClick={() => handleStatusFilterChange('UNDELIVERED')}
                      >
                        Undelivered
                      </button>
                    </li>
                    <li>
                      <button 
                        type="button" 
                        className="dropdown-item"
                        onClick={() => handleStatusFilterChange('PAID')}
                      >
                        Paid
                      </button>
                    </li>
                    <li>
                      <button 
                        type="button" 
                        className="dropdown-item"
                        onClick={() => handleStatusFilterChange('DELIVERED')}
                      >
                        Delivered
                      </button>
                    </li>
                    <li>
                      <button 
                        type="button" 
                        className="dropdown-item"
                        onClick={() => handleStatusFilterChange('ARCHIVED')}
                      >
                        Archived
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <DataTable
              value={filteredOrders}
              emptyMessage="No purchase orders found."
              sortMode="multiple"
              paginator
              rows={10}
              stripedRows
              loading={loading}
              tableStyle={{ minWidth: '50rem' }}
              stateStorage="session"
              stateKey="purchase-order-state"
            >
              <Column 
                field="date" 
                header="DATE" 
                sortable 
                body={(rowData) => formatDate(rowData.date)}
              />
              <Column field="poNumber" header="PO NUMBER" sortable />
              <Column
                field="grandTotal"
                header="Total AMOUNT"
                body={(rowData) => formatCurrency(rowData.grandTotal)}
                sortable
              />
              <Column field="department" header="Department" sortable />
              <Column 
                header="SUPPLIER" 
                body={supplierTemplate} 
                sortable 
                sortField="supplierInfo.name"
              />
              <Column 
                field="status" 
                header="STATUS" 
                body={statusBodyTemplate} 
                sortable 
              />
              <Column 
                header="OPTIONS" 
                body={actionTemplate} 
                style={{ width: '12rem' }} 
              />
            </DataTable>
          </div>
        </div>
      </div>

      {/* Comments Dialog for Approval/Rejection */}
      <Dialog
        header={`${approvalAction === 'approve' ? 'Approve' : 'Reject'} Purchase Order`}
        visible={commentsDialogVisible}
        style={{ width: '500px' }}
        footer={commentsDialogFooter}
        onHide={() => setCommentsDialogVisible(false)}
      >
        <div className="field">
          <label htmlFor="comments">Comments (Optional)</label>
          <InputTextarea
            id="comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={5}
            cols={30}
            className="w-100"
            placeholder="Enter any comments regarding your decision..."
          />
        </div>
      </Dialog>
    </>
  );
};

export default PurchaseOrderTab;